/**
 * Song-feedback client (#250).
 *
 * Thin wrappers over the Fly backend's /artifact-feedback/{jobId}
 * GET + PATCH endpoints. The feedback record stores three independent
 * pieces of per-song user data:
 *
 *   - rating: 1-5 stars at the song level (for sorting / favorites)
 *   - edits: user-corrected translation text per (language, verseIdx)
 *     - these survive re-process because they live in a sidecar
 *       JSON that the pipeline never touches
 *     - frontend overlays them on top of the model output at render
 *   - verseFlags: 👍 / 👎 per (language, verseIdx) for quality triage
 *
 * The PATCH endpoint deep-merges, so we send only what's changing —
 * the user editing one verse doesn't blow away the rating, etc.
 *
 * Routes through the Cloudflare /api/fly/* proxy (same as the songs
 * list) so the browser doesn't need to know the upstream Fly URL.
 */

import { apiHeaders, fastapiBase } from "./fastapi-url";

export type VerseFlag = "up" | "down";

export interface SongFeedback {
  rating?: number | null;
  /** TRANSLATION edits.
   *  language → { verseIndex (as string) → user-corrected text }.
   *  When used in PATCH bodies, an empty-string value means
   *  "clear this verse's edit". The server treats it as a delete. */
  edits?: Record<string, Record<string, string>>;
  /** LYRICS (source-script) edits. #254 — same shape as translation
   *  edits but keyed by SCRIPT ("devanagari" / "tamil" / "iast" / ...)
   *  instead of by target language. User correcting whisper's
   *  mistranscription (e.g. मनसाँ → मनसा). */
  lyrics_edits?: Record<string, Record<string, string>>;
  /** language → { verseIndex (as string) → "up" | "down" }
   *  Note: when used in PATCH bodies, an empty-string value means
   *  "clear this verse's flag". The server treats it as a delete.
   *  We type the value as `string` (rather than `VerseFlag`) so the
   *  same shape can be used for both reads and writes — `getVerseFlag`
   *  narrows it back to `VerseFlag | null` at read sites. */
  verse_flags?: Record<string, Record<string, string>>;
  /** #264 — User overrides for song metadata fields.
   *  Flat key→value: `{ singer: "Sooryagayathri", composer: "...", raga: "..." }`.
   *  The queue row captures these at upload time but some (like
   *  `singer`) aren't auto-extracted and need user input. This sidecar
   *  lets the user fill or correct them after the fact.
   *  When used in PATCH bodies, an empty-string value DELETES the
   *  override (fall back to whatever the queue row has). */
  meta_overrides?: Record<string, string>;
  /** #267 — Cached canonical lyrics from /songs/identify (Gemini-grounded).
   *  Persisted after the first successful fetch so subsequent page
   *  loads use the cache instead of re-calling Gemini (each grounded
   *  call costs ~$0.035). PATCH semantic: REPLACE — sending [] clears
   *  the cache and forces the next fetch. */
  canonical_lyrics?: Array<{
    section: string;
    sanskrit: string;
    transliteration: string;
    translation: string;
  }>;
  /** ISO8601, server-set on every write. Useful for cache invalidation. */
  updated_at?: string;
}

/** Empty default so callers can assume a shape without null-guards. */
export const EMPTY_FEEDBACK: SongFeedback = {
  rating: null,
  edits: {},
  verse_flags: {},
};

export async function fetchFeedback(jobId: string): Promise<SongFeedback> {
  const res = await fetch(`${fastapiBase()}/artifact-feedback/${encodeURIComponent(jobId)}`, {
    headers: apiHeaders({ Accept: "application/json" }),
  });
  if (res.status === 404) {
    // Treat missing as empty — feedback is a sidecar, not a required
    // resource.
    return { ...EMPTY_FEEDBACK };
  }
  if (!res.ok) {
    throw new Error(`feedback fetch failed: ${res.status} ${res.statusText}`);
  }
  return (await res.json()) as SongFeedback;
}

/**
 * Patch a partial feedback payload. The backend deep-merges, so this
 * is safe to call with a tiny diff (e.g. just `{rating: 4}` or just
 * `{edits: {english: {"22": "..."}}}`).
 *
 * To CLEAR a per-verse edit/flag, send an empty string as the value.
 * The server treats that as "delete this slot".
 */
export async function patchFeedback(
  jobId: string,
  partial: SongFeedback,
): Promise<SongFeedback> {
  const res = await fetch(`${fastapiBase()}/artifact-feedback/${encodeURIComponent(jobId)}`, {
    method: "PATCH",
    headers: apiHeaders({
      Accept: "application/json",
      "Content-Type": "application/json",
    }),
    body: JSON.stringify(partial),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => "");
    throw new Error(`feedback patch failed: ${res.status} ${detail.slice(0, 200)}`);
  }
  return (await res.json()) as SongFeedback;
}

/** Helper: get a user-edited translation for a verse, or null if none. */
export function getEditedTranslation(
  feedback: SongFeedback | null | undefined,
  language: string,
  verseIdx: number,
): string | null {
  const v = feedback?.edits?.[language]?.[String(verseIdx)];
  return v && v.trim().length > 0 ? v : null;
}

/** Helper: get a user-edited LYRICS line for a verse in a given script,
 *  or null if no edit. #254 — mirrors getEditedTranslation but for the
 *  source-script column. */
export function getEditedLyrics(
  feedback: SongFeedback | null | undefined,
  script: string,
  verseIdx: number,
): string | null {
  const v = feedback?.lyrics_edits?.[script]?.[String(verseIdx)];
  return v && v.trim().length > 0 ? v : null;
}

/** Helper: get a verse-quality flag for a verse, or null if none.
 *  Narrows the stored `string` back to `VerseFlag | null` — anything
 *  unrecognised (e.g. an empty string sneaking in) is treated as null. */
export function getVerseFlag(
  feedback: SongFeedback | null | undefined,
  language: string,
  verseIdx: number,
): VerseFlag | null {
  const v = feedback?.verse_flags?.[language]?.[String(verseIdx)];
  if (v === "up" || v === "down") return v;
  return null;
}
